<template>
  <div>
    <DataTableFilter ref="dataTableFilter"
                     :options="options"
                     class="mt-3"/>

    <div class="row align-items-center">
      <div class="col col-md-3 col-xl-2">
        <InputGroup v-model="nameSearch"
                    class="mb-0"
                    :placeholder="$t('search_by_name')"/>
      </div>
      <div class="col col-md-3 col-xl-2"
           v-if="!rights.includes('hide_patient_phone')">
        <InputGroup v-model="phoneSearch"
                    class="mb-0"
                    :placeholder="$t('search_by_phone')"/>
      </div>
      <div class="col-auto">
        <CheckboxGroup fid="thirtyDays"
                       v-model="lastThirtyDays"
                       class="mb-0"
                       :label="$t('last_thirty_days')"
                       :placeholder="$t('last_thirty_days')"/>
      </div>
      <div class="col-auto">
        <CheckboxGroup fid="tasksToday"
                       v-model="withTasksForToday"
                       class="mb-0"
                       :label="$t('has_today_task')"
                       :placeholder="$t('search_by_phone')"/>
      </div>
      <div class="col-auto">
        <CheckboxGroup fid="noTasks"
                       :label="$t('without_tasks')"
                       class="mb-0"
                       v-model="withoutTasks"
                       :placeholder="$t('search_by_phone')"/>
      </div>
    </div>

    <div style="overflow-x: auto; overflow-y: hidden" class="pt-3">

      <div class="row flex-nowrap no-gutters">
        <div v-for="(l, lind) in CRMStages"
             class="col"
             :key="`lead-${lind}`">
          <div class="lead-wrap h-100"
               :class="{ 'ml-3': lind > 0 }">
            <div>
              {{ $t(l.title) }} | <span :class="{ 'text-danger': !!leads[l.id] }">{{ totalsComputed[l.id] || 0 }}</span>
              <div class="separator w-100 mt-2 mb-3" :style="`background-color: ${l.color}`"/>
            </div>

            <div class="overflow-auto list-group-fixed-height">
              <draggable
                  class="list-group"
                  :list="leads[l.id]"
                  group="leads"
                  item-key="id"
                  :data-status="l.id"
                  @change="changed"
                  :move="leadMoved">
                <template #item="{ element }">
                  <div @click="showLeadModal(element)"
                       :data-id="element.id"
                       class="lead-card card p-md-3 p-2 pointer">
                    <div class="row no-gutters">
                      <div class="col">
                        <div class="font-weight-bold pr-1">
                          {{ element.patient_name }}
                          <span v-if="element.urgent"
                                class="notification-bubble mr-md-3 mt-md-3 mr-2 mt-2"></span>
                        </div>
                        <div v-if="!rights.includes('hide_patient_phone')">{{ element.phone }}</div>
                        <div v-if="element.patient_id && element.patient && element.patient.full_name">
                          <router-link :to="{ name: 'PatientVisits', params: { id: element.patient_id } }">
                            {{ element.patient.full_name }}
                          </router-link>
                        </div>
                        <div v-html="element.comment"/>
                        <div>{{ element.reason }}</div>
                        <div>{{ $t('date_add') }}: {{ formatDateTime(element.created_at) }}</div>
                        <div v-if="element.call_date" class="text-danger">
                          {{ $t('recall') }}: {{ formatDate(element.call_date) }}
                        </div>
                        <div v-if="listLeadResponsible(element)">
                          {{ $t('responsible') }}: {{ listLeadResponsible(element) }}
                        </div>
                      </div>
                      <div class="col-auto">
                        <TasksBubble :tasks="mergeTasks(element)"
                                     class="task-bubbles-relative"/>
                      </div>
                    </div>

                  </div>
                </template>
              </draggable>
              <ScrollToEventElement v-if="initiated"
                                    @visible="loadOnScroll(l)">&nbsp;</ScrollToEventElement>
            </div>
          </div>
        </div>
      </div>

      <LeadShowModal ref="leadShowModal"
                     @reloadLead="reloadLead"/>

    </div>
  </div>
</template>

<script setup>
const CACHE_TTL = 5 * 60 * 1000
let pageCache = null
const useCaching = true
// let isRestoring = false

import { EloquentService } from "@/services/api/eloquent.api.service"
import LeadShowModal from "@/components/parts/crm/LeadShowModal"
import { useStore } from "vuex"
import options from "@/data/crm/lead"
import draggable from 'vuedraggable'
import DataTableFilter from "@/components/parts/datatable/DataTableFilter"
import { formatDate, formatDateTime } from "@/extensions/filters/filters"
import InputGroup from "@/components/form/InputGroup.vue"
import CheckboxGroup from "@/components/form/CheckboxGroup.vue"
import { onlyNumbers } from "@/services/phone.helper"
import {useRoute } from "vue-router"
import {computed, getCurrentInstance, nextTick, onBeforeMount, onBeforeUnmount, onMounted, ref, watch} from "vue"
import {useHead} from "@unhead/vue"
import {appendFormdata, copyObject} from "@/extensions/prototypes/prototypes"
import ScrollToEventElement from "@/components/parts/general/ScrollToEventElement.vue"
import TasksBubble from "@/components/parts/crm/TasksBubble.vue"

const store = useStore()
const route = useRoute()

let i18n = null
let noty = null
let bvModal = null

const title = ref('')

useHead({
  title: () => title.value,
})

onBeforeMount(() => {
  const instance = (getCurrentInstance())
  i18n = instance?.ctx?.$i18n
  noty = instance?.appContext?.app?.noty
  bvModal = instance?.ctx?._bv__modal
  title.value = i18n.t('sales_funnel')
  draggable.compatConfig = { MODE: 3 }
})

const leads = ref({})
const totals = ref({})
const leadsLoading = ref({})
const statusPages = ref({})
const initiated = ref(false)
const dataToSend = ref(null)
const timer = ref(null)
const nameSearch = ref('')
const phoneSearch = ref('')
const withoutTasks = ref(false)
const withTasksForToday = ref(false)
const lastThirtyDays = ref(false)
const forceLoaded = ref([])
const updateLoading = ref(false)
const dataTableFilter = ref(null)
const leadShowModal = ref(null)

const filters = computed(() => store.state.datatable.filters)
const filtersCache = computed(() => store.state.datatable.filtersCache)
const hasFilters = computed(() => Object.keys(filters.value).length > 0)
const CRMStages = computed(() => store.state.auth.CRMStages)
const listLeadResponsible = computed(() => store.getters.listLeadResponsible)
const counter = computed(() => store.state.datatable.dataTableCounter)
const rights = computed(() => store.state.auth.rights)
const leadsFlat = computed(() => Object.values(leads.value).flat())

const loadData = async (statusId = null, lastUpdated = null, leadId = null) => {
  try {
    let page = 1
    let tmpFilters
    if(!leadId) {
      if(!statusId) initialLeadsState()
      if(statusId) {
        if(leadsLoading.value[statusId]) return
        if(!statusPages.value[statusId]) {
          statusPages.value[statusId] = 0
        }
        statusPages.value[statusId]++
        page = statusPages.value[statusId]
        leadsLoading.value[statusId] = true
      }
      if(page === 1) {
        if(!statusId) {
          leads.value = {}
        } else {
          leads.value[statusId] = []
        }
      }
      tmpFilters = copyObject(filters.value)
      if(Object.keys(tmpFilters).length === 0) {
        tmpFilters = {
          custom: {
            id: 'custom',
            condition: true
          }
        }
      }
      if(statusId) {
        tmpFilters.status_id = {
          id: 'status_id',
          type: 'equals',
          condition: statusId
        }
      }
      if(lastUpdated) {
        tmpFilters.updated_at = {
          id: 'updated_at',
          type: 'less',
          condition: lastUpdated
        }
      }
    } else {
      tmpFilters = {
        id: {
          id: 'id',
          type: 'equals',
          condition: leadId
        }
      }
    }
    const data = await EloquentService.dataTable('lead', page, {
      pagination: hasFilters.value || !statusId ? 500 : 10,
      filters: tmpFilters,
      order: 'updated_at',
      sort: 'desc',
    })
    if(leadId) {
      const lead = data?.data?.result?.data[0] || null
      if(lead) replaceLead(lead)
    } else {
      data.data.result.data.forEach(lead => {
        if(!leads.value[lead.status_id]) {
          leads.value[lead.status_id] = []
        }
        leads.value[lead.status_id].push(lead)
      })
      for(let stat in leads.value) { // removing duplicates
        leads.value[stat] = Array.from(new Map(leads.value[stat].map(item => [item.id, item])).values())
      }
    }

  } catch (e) {
    console.log(e)
  } finally {
    if(statusId) {
      leadsLoading.value[statusId] = false
    }
    forceLoadMore().then()
  }
}

const replaceLead = (lead) => {
  const list = leads.value[lead.status_id]
  if(!list) return
  const index = list.findIndex(l => String(l.id) === String(lead.id))
  if(index !== -1) list.splice(index, 1, lead)
}

const showLeadModal = (lead) => {
  store.commit('setLead', copyObject(lead))
  bvModal.show('lead-show-modal')
}

const forceLoadMore = async () => {
  CRMStages.value.forEach(status => {
    if((leads.value[status.id] || []).length < 10 && !leadsLoading.value[status.id] && !forceLoaded.value.includes(status.id)) {
      forceLoaded.value.push(status.id)
      loadOnScroll(status)
    }
  })
}

const leadMoved = (e) => {
  const leadId = String(e.dragged.getAttribute('data-id'))
  const newStatus = e.to.getAttribute('data-status')
  const oldStatus = e.from.getAttribute('data-status')
  const lead = leads.value[oldStatus].find(x => String(x.id) === leadId)
  let formData = new FormData()
  formData = appendFormdata(formData, {
    status_id: newStatus,
    source: lead.source,
    fname: lead.fname,
    phone: lead.phone,
  })
  dataToSend.value = { leadId, formData, oldStatus, newStatus }
}

const changed = async () => {
  if(!dataToSend.value || updateLoading.value) return
  try {
    updateLoading.value = true
    if(dataToSend.value) {
      totals.value[dataToSend.value.oldStatus]--
      totals.value[dataToSend.value.newStatus]++
    }
    await EloquentService.update(
        'lead',
        dataToSend.value.leadId,
        dataToSend.value.formData,
        false,
        false
    )
    if(dataToSend.value) {
      loadData(dataToSend.value.newStatus, null).then()
    }
  } catch (e) {
    console.log(e)
    noty.error(e?.response?.data?.message || i18n.t('error'))
  } finally {
    updateLoading.value = false
  }
  dataToSend.value = null
}

const initialLeadsState = () => {
  leads.value = {}
  CRMStages.value.forEach(status => {
    leads.value[status.id] = []
    statusPages[status.id] = 0
  })
}

const loadStatuses = async () => {
  await store.dispatch('getCRMStages')
  CRMStages.value.forEach(status => {
    totals.value[status.id] = status.leads_count || 0
  })
}

const loadOnScroll = async (val) => {
  const statusId = val.id
  const leadsLength = leads.value[statusId]?.length || 0
  const total = totalsComputed.value[statusId] || 0
  if(leadsLength >= total) return
  await loadData(statusId, null)
}

const totalsComputed = computed(() => {
  if(hasFilters.value) {
    const res = {}
    CRMStages.value.forEach(status => {
      res[status.id] = leads.value[status.id]?.length
    })
    return res
  }
  return totals.value
})

onMounted(async () => {
  store.commit("setViewModel", 'lead')
  store.commit('setEloquentOptions', options)
  adjustFillerHeight()
  window.addEventListener("resize", adjustFillerHeight)
  window.addEventListener("load", adjustFillerHeight)

  pageCache = copyObject(store.state.crm.funnelApplicationsCache)

  if (pageCache && Date.now() - pageCache.timestamp < CACHE_TTL && useCaching) {
    Array.from(document.querySelectorAll('.list-group-fixed-height')).forEach((el, i) => {
      if (pageCache.scrollPositions?.[i] !== undefined) {
        el.scrollTop = pageCache.scrollPositions[i]
      }
    })
    if (pageCache.modalOpen && pageCache.openedLead) {
      store.commit('setLead', pageCache.openedLead)
      bvModal.show('lead-show-modal')
    }
  }

  store.commit('setFilters', {})
  await loadStatuses()
  lastThirtyDays.value = filtersCache.value?.CRMSalesFunnelApplications?.last_thirty_days?.condition === 1
  nameSearch.value = filtersCache.value?.CRMSalesFunnelApplications?.patient_id?.condition || ''
  phoneSearch.value = filtersCache.value?.CRMSalesFunnelApplications?.phone_formatted?.condition || ''
  initiated.value = true
  if(lastThirtyDays.value || nameSearch.value || phoneSearch.value) { // we need it because filters are taken from url, so refresh from watchers will not reload, cause it won't change route
    await nextTick()
    await loadData()
  }
})

const reloadLead = (leadId) => {
  const lead = leadsFlat.value.find(c => Number(c.id) === Number(leadId))
  if(!lead?.patient_id) {
    loadData(null, null, leadId)
    return
  }
  const leadsToReload = leadsFlat.value.filter(c => Number(c.patient_id) === Number(lead?.patient_id))
  for(const lead of leadsToReload) {
    loadData(null, null, lead.id)
  }
}

const mergeTasks = (element) => {
  return [...new Map(
      [
        ...(element?.patient?.patient_tasks || []),
        ...(element?.tasks || [])
      ].map(item => [item.id, item])
  ).values()]
}

watch(route, () => {
  // if (isRestoring) return
  // if (to.name !== 'CRMSalesFunnelApplications') return
  statusPages.value = {}
  forceLoaded.value = []
  loadData()
})

watch(counter, async () => {
  forceLoaded.value = []
  await loadStatuses()
  await loadData()
})

watch(nameSearch, (val) => {
  // if (isRestoring) return
  if(timer.value) clearTimeout(timer.value)
  timer.value = setTimeout(() => {
    const dataFilter = dataTableFilter.value
    if(val) {
      dataFilter.filters.patient_id = {
        "id": "patient_id",
        "title": "patient",
        "condition": val,
        "type": "text",
        "ctype": "like"
      }
    } else {
      delete dataFilter.filters.patient_id
    }
    forceLoaded.value = []
    dataFilter.refresh()
  }, 600)
})

watch(phoneSearch, (val) => {
  // if (isRestoring) return
  val = onlyNumbers(val)
  if(timer.value) clearTimeout(timer.value)
  timer.value = setTimeout(() => {
    const dataFilter = dataTableFilter.value
    if(val) {
      dataFilter.filters.phone_formatted = {
        "id": "phone_formatted",
        "title": "phone",
        "condition": val,
        "type": "text",
        "ctype": "like"
      }
    } else {
      delete dataFilter.filters.phone_formatted
    }
    forceLoaded.value = []
    dataFilter.refresh()
  }, 600)
})


watch(() => withoutTasks.value, (val) => {
  // if (isRestoring) return
  const dataFilter = dataTableFilter.value
  if(val) {
    dataFilter.filters.has_tasks = {
      "id": "has_tasks",
      "title": "has_tasks",
      "condition": 0,
      "type": "boolean",
      "ctype": "equals"
    }
  } else {
    delete dataFilter.filters.has_tasks
  }
  forceLoaded.value = []
  dataFilter.refresh()
})

watch(() => withTasksForToday.value, (val) => {
  // if (isRestoring) return
  const dataFilter = dataTableFilter.value
  if(val) {
    dataFilter.filters.has_today_tasks = {
      "id": "has_today_tasks",
      "title": "has_today_tasks",
      "condition": 1,
      "type": "boolean",
      "ctype": "equals"
    }
  } else {
    delete dataFilter.filters.has_today_tasks
  }
  forceLoaded.value = []
  dataFilter.refresh()
})

watch(lastThirtyDays, (val) => {
  // if (isRestoring) return
  const dataFilter = dataTableFilter.value
  if(val) {
    dataFilter.filters.last_thirty_days = {
      "id": "last_thirty_days",
      "title": "last_thirty_days",
      "condition": 1,
      "type": "boolean",
      "ctype": "equals"
    }
  } else {
    delete dataFilter.filters.last_thirty_days
  }
  forceLoaded.value = []
  dataFilter.refresh()
})

const adjustFillerHeight = () => {
  const fillers = document.getElementsByClassName("list-group-fixed-height")
  const windowHeight = window.innerHeight
  fillers.forEach(filler => {
    const positionFromTop = filler.getBoundingClientRect().top
    filler.style.height = (windowHeight - positionFromTop) + "px"
  })
}

onBeforeUnmount(() => {
  const scrollPositions = {}
  Array.from(document.querySelectorAll('.list-group-fixed-height')).forEach((el, i) => {
    scrollPositions[i] = el.scrollTop
  })
  const dataToBeCached = {
    leads: leads.value,
    totals: totals.value,
    statusPages: statusPages.value,
    forceLoaded: [...forceLoaded.value],
    nameSearch: nameSearch.value,
    phoneSearch: phoneSearch.value,
    withoutTasks: withoutTasks.value,
    withTasksForToday: withTasksForToday.value,
    lastThirtyDays: lastThirtyDays.value,
    scrollPositions,
    modalOpen: leadShowModal.value?.show ?? false,
    openedLead: store.state.crm.lead,
    timestamp: Date.now(),
  }
  store.commit('setFunnelApplicationsCache', copyObject(dataToBeCached))
  store.commit('setFilters', {})
  window.removeEventListener("resize", adjustFillerHeight)
  window.removeEventListener("load", adjustFillerHeight)
})
</script>

<style scoped lang="scss">
.separator {
  height: 2px;
}
.lead-wrap {
  min-width: 220px;
}
.lead-card {
  border-radius: 8px;
  &:hover {
    background-color: $light-blue;
  }
}
@media screen and (max-width: 768px) {
  .lead-wrap {
    min-width: 150px;
  }
}
</style>
